import React, { useState, useEffect, useMemo } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import { useNavigate } from "react-router-dom";
import { Response } from "../../types/Api.ts";
import UpdatePacienteDrawer from "../../components/Modals/UpdatePacienteDrawer/index.tsx";
import CreatePacienteDrawer from "../../components/Modals/CreatePacienteDrawer/index.tsx";
import ConfirmDeleteDialog from "../../components/Modals/ConfirmDeleteDialog/index.tsx";
import { maskCpfParcial } from "../../utils/cpf.util.ts";
import { formatBrazilPhone } from "../../utils/phone.util.ts";
import Button from "../../ui/Button.tsx";
import SearchField from "../../ui/SearchField.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import "../../styles/design-system.css";

interface Paciente {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  cpf: string;
  dataNascimento?: string;
}

// "Antônio Marques Filho" -> "AM" (primeira letra do primeiro e do último nome).
const getIniciais = (nome: string) => {
  const partes = (nome || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  const primeira = partes[0][0] || "";
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (primeira + ultima).toUpperCase();
};

// Idade calculada no client a partir da data de nascimento (string
// "YYYY-MM-DD" vinda do input type=date) — leva em conta se o aniversário
// deste ano já passou, não só a diferença de anos.
const calcularIdade = (dataNascimento?: string): number | null => {
  if (!dataNascimento) return null;
  const nascimento = new Date(dataNascimento);
  if (Number.isNaN(nascimento.getTime())) return null;
  const hoje = new Date();
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const aniversarioJaPassou =
    hoje.getMonth() > nascimento.getMonth() ||
    (hoje.getMonth() === nascimento.getMonth() && hoje.getDate() >= nascimento.getDate());
  if (!aniversarioJaPassou) idade -= 1;
  return idade;
};

const ListagemPaciente = () => {
  const navigate = useNavigate();
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [busca, setBusca] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [currentPaciente, setCurrentPaciente] = useState<Paciente | null>(null);
  const [pacienteParaExcluir, setPacienteParaExcluir] = useState<Paciente | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcluindo, setIsExcluindo] = useState(false);

  useEffect(() => {
    fetchPacientes();
  }, []);

  const fetchPacientes = async () => {
    try {
      setIsLoading(true);
      const response = await handleService({ endpoint: "/paciente" });
      const lista: Paciente[] = Array.isArray(response) ? response : response.data || [];
      setPacientes(lista);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditar = (paciente: Paciente) => {
    setCurrentPaciente(paciente);
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setCurrentPaciente(null);
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setCurrentPaciente(null);
  };

  const onUpdate = (response: Response<Paciente>) => {
    const paciente = response.data;
    if (paciente) {
      setPacientes((prev) => prev.map((p) => (p.id === paciente.id ? paciente : p)));
      setIsEditOpen(false);
    }
  };

  const onCreated = (response: Response<Paciente>) => {
    const paciente = response.data;
    if (paciente) {
      setPacientes((prev) => [...prev, paciente]);
      setIsCreateOpen(false);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!pacienteParaExcluir) return;
    try {
      setIsExcluindo(true);
      await handleService({ endpoint: `/paciente/${pacienteParaExcluir.id}`, method: MethodsEnum.DELETE });
      setPacientes((prev) => prev.filter((p) => p.id !== pacienteParaExcluir.id));
      setPacienteParaExcluir(null);
    } catch (error) {
    } finally {
      setIsExcluindo(false);
    }
  };

  const filtrados = useMemo(
    () => pacientes.filter((p) => p.nome?.toLowerCase().includes(busca.toLowerCase())),
    [pacientes, busca]
  );

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Pacientes</h1>
          <p>Visualize e gerencie os pacientes da sua clínica.</p>
        </div>
        <div className="ds-page-head-actions">
          <Button variant="primary" icon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setIsCreateOpen(true)}>
            Novo paciente
          </Button>
        </div>
      </div>

      <div className="ds-toolbar">
        <SearchField value={busca} onChange={setBusca} placeholder="Buscar por nome..." />
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>CPF</th>
              <th>E-mail</th>
              <th>Telefone</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={5} />
            ) : filtrados.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState title="Nenhum paciente encontrado" description="Cadastre o primeiro paciente para começar." actionLabel="Novo paciente" onAction={() => setIsCreateOpen(true)} />
                </td>
              </tr>
            ) : (
              filtrados.map((paciente) => {
                const idade = calcularIdade(paciente.dataNascimento);
                return (
                  <tr key={paciente.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div className="ds-avatar">{getIniciais(paciente.nome)}</div>
                        <div>
                          <div className="ds-cell-title">{paciente.nome}</div>
                          <div className="ds-cell-sub">{idade !== null ? `${idade} anos` : "Idade não informada"}</div>
                        </div>
                      </div>
                    </td>
                    <td>{maskCpfParcial(paciente.cpf)}</td>
                    <td>{paciente.email || "—"}</td>
                    <td>{formatBrazilPhone(paciente.telefone) || paciente.telefone || "—"}</td>
                    <td>
                      <div className="ds-row-actions">
                        <button className="ds-icon-btn" onClick={() => navigate(`/paciente/${paciente.id}/perfil`)} title="Visualizar">
                          <VisibilityOutlinedIcon fontSize="small" />
                        </button>
                        <button className="ds-icon-btn" onClick={() => handleEditar(paciente)} title="Editar">
                          <EditOutlinedIcon fontSize="small" />
                        </button>
                        <button className="ds-icon-btn" onClick={() => setPacienteParaExcluir(paciente)} title="Excluir">
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <UpdatePacienteDrawer isOpen={isEditOpen} onClose={handleCloseEdit} paciente={currentPaciente} onUpdate={onUpdate} />
      <UpdatePacienteDrawer isOpen={isViewOpen} onClose={handleCloseView} paciente={currentPaciente} readOnly />
      <CreatePacienteDrawer open={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={onCreated} />

      <ConfirmDeleteDialog
        open={!!pacienteParaExcluir}
        title="Excluir Paciente"
        message="Tem certeza que deseja excluir este paciente cadastrado? Esta ação não poderá ser desfeita."
        loading={isExcluindo}
        onCancel={() => setPacienteParaExcluir(null)}
        onConfirm={handleConfirmarExclusao}
      />
    </div>
  );
};

export default ListagemPaciente;
